import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  enableProdMode,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
} from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthClientConfig, AuthModule } from '@auth0/auth0-angular';
import { Capacitor } from '@capacitor/core';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { SecureStorage } from '@ionic-native/secure-storage/ngx';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideNgIdle } from '@ng-idle/core';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { httpInterceptorProviders } from './app/interceptors';
import { FormatCamelcasePipe } from './app/pipes/format-camelcase/format-camelcase.pipe';
import { FormatTelephonePipe } from './app/pipes/format-telephone/format-telephone.pipe';
import { FormatZipcodePipe } from './app/pipes/format-zipcode/format-zipcode';
import { HttpHelperService } from './app/services/http-helper/http-helper.service';
import { environment } from './environments/environment';
import { NativeSettingsService } from './app/services/native-settings/native-settings.service';
import { OpenNativeSettings } from '@ionic-native/open-native-settings/ngx';

const configInitializer = (authConfig: AuthClientConfig) => {
  const devEnvironmentString = localStorage.getItem('devEnvironmentObj');
  if (devEnvironmentString) {
    const devEnvironmentObj = JSON.parse(devEnvironmentString);
    if (Capacitor.isNativePlatform()) {
      authConfig.set({
        domain: devEnvironmentObj.auth0_mobile.domain,
        clientId: devEnvironmentObj.auth0_mobile.clientId,
        cacheLocation: 'localstorage', // required to allow using refresh tokens
        useRefreshTokens: true,
        useRefreshTokensFallback: false,
        authorizationParams: {
          redirect_uri: devEnvironmentObj.auth0_mobile.callBackUrl,
          audience: devEnvironmentObj.auth0_mobile.audience,
          scope: 'openid profile email offline_access',
        },
      });
    } else {
      authConfig.set({
        domain: devEnvironmentObj.auth0.domain,
        clientId: devEnvironmentObj.auth0.clientId,
        cacheLocation: 'localstorage', // required to allow using refresh tokens
        useRefreshTokens: true,
        useRefreshTokensFallback: false,
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: devEnvironmentObj.auth0.audience,
          prompt: 'login',
          scope: 'openid profile email offline_access',
        },
      });
    }
  } else {
    if (Capacitor.isNativePlatform()) {
      authConfig.set({
        domain: environment.auth0_mobile.domain,
        clientId: environment.auth0_mobile.clientId,
        cacheLocation: 'localstorage', // required to allow using refresh tokens
        useRefreshTokens: true,
        useRefreshTokensFallback: false,
        authorizationParams: {
          redirect_uri: environment.auth0_mobile.callBackUrl,
          audience: environment.auth0_mobile.audience,
          scope: 'openid profile email offline_access',
        },
      });
    } else {
      authConfig.set({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        cacheLocation: 'localstorage', // required to allow using refresh tokens
        useRefreshTokens: true,
        useRefreshTokensFallback: false,
        authorizationParams: {
          redirect_uri: window.location.origin,
          audience: environment.auth0.audience,
          prompt: 'login',
          scope: 'openid profile email offline_access',
        },
      });
    }
  }
};

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      useSetInputAPI: true,
      innerHTMLTemplatesEnabled: true,
    }),

    provideRouter(routes, withPreloading(PreloadAllModules)),
    importProvidersFrom(AuthModule.forRoot(), IonicStorageModule.forRoot()),
    provideAppInitializer(() => configInitializer(inject(AuthClientConfig))),
    HttpHelperService,
    FileOpener,
    provideHttpClient(withInterceptorsFromDi()),
    httpInterceptorProviders,
    InAppBrowser,
    OpenNativeSettings,
    NativeSettingsService,
    SecureStorage,
    provideNgIdle(),
    FormatTelephonePipe,
    FormatZipcodePipe,
    FormatCamelcasePipe,
  ],
}).catch((err) => console.log(err));
